import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, Package, Truck, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrderSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/shop');
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setPaymentStatus('timeout');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
          withCredentials: true
        });

        if (response.data.payment_status === 'paid') {
          setPaymentStatus('paid');
          setOrder(response.data.order);
          setLoading(false);
          return;
        } else if (response.data.status === 'expired') {
          setPaymentStatus('expired');
          setLoading(false);
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    };

    pollPaymentStatus();
  }, [sessionId, navigate]);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-cream" data-testid="order-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-forest mx-auto mb-6"></div>
          <p className="font-manrope text-dark/70 text-lg">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'expired' || paymentStatus === 'timeout') {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-cream" data-testid="order-failed">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Package className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="font-playfair text-3xl font-bold text-dark mb-4">Payment Issue</h1>
          <p className="font-manrope text-dark/70 mb-8">
            There was an issue with your payment. Please try again or contact support.
          </p>
          <Link to="/cart" className="btn-primary" data-testid="try-again-btn">
            Try Again
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-cream" data-testid="order-success-page">
      <div className="container-custom py-12">
        <div className="max-w-2xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-forest" />
            </div>
            <h1 className="font-playfair text-4xl font-bold text-dark mb-4" data-testid="success-title">
              Thank You for Your Order!
            </h1>
            <p className="font-manrope text-dark/70 text-lg">
              Your order has been confirmed and will be delivered soon.
            </p>
          </div>

          {/* Order Details */}
          {order && (
            <div className="bg-white rounded-2xl p-8 border border-forest/10 mb-8" data-testid="order-details">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-playfair text-2xl font-bold text-dark">Order Details</h2>
                <span className="badge-organic">{order.status}</span>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between font-manrope">
                  <span className="text-dark/60">Order ID</span>
                  <span className="font-medium text-dark" data-testid="order-id">{order.order_id}</span>
                </div>
                <div className="flex justify-between font-manrope">
                  <span className="text-dark/60">Date</span>
                  <span className="text-dark">{new Date(order.created_at).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Items */}
              <div className="border-t border-forest/10 pt-6 mb-6">
                <h3 className="font-manrope font-bold text-dark mb-4">Items</h3>
                <div className="space-y-3">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex justify-between font-manrope">
                      <span className="text-dark/70">
                        {item.name} x {item.quantity}
                        <span className="text-dark/40 ml-2">({item.subscription_type})</span>
                      </span>
                      <span className="text-dark">${item.item_total.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-forest/10 pt-4 space-y-2">
                <div className="flex justify-between font-manrope">
                  <span className="text-dark/60">Subtotal</span>
                  <span className="text-dark">${order.subtotal.toFixed(2)}</span>
                </div>
                {order.discount > 0 && (
                  <div className="flex justify-between font-manrope" data-testid="order-discount-row">
                    <span className="text-forest">{order.discount_label || 'Discount'}</span>
                    <span className="text-forest">-${order.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-manrope">
                  <span className="text-dark/60">Delivery</span>
                  <span className="text-dark">{order.delivery_fee === 0 ? 'Free' : `$${order.delivery_fee.toFixed(2)}`}</span>
                </div>
                {order.preference_fee > 0 && (
                  <div className="flex justify-between font-manrope" data-testid="order-preference-fee-row">
                    <span className="text-dark/60">Thermal Porch Protection</span>
                    <span className="text-dark">+${order.preference_fee.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-playfair text-xl font-bold pt-2">
                  <span className="text-dark">Total</span>
                  <span className="text-dark" data-testid="order-total">${order.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Delivery Info */}
          <div className="bg-forest/5 rounded-2xl p-8 border border-forest/10 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Truck className="w-6 h-6 text-forest" />
              <h3 className="font-playfair text-xl font-bold text-dark">Delivery Information</h3>
            </div>
            <p className="font-manrope text-dark/70">
              Your order will be delivered within 2-3 business days. You'll receive an email with tracking information once your order ships.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/dashboard" className="btn-primary justify-center" data-testid="view-orders-btn">
              View My Orders
            </Link>
            <Link to="/shop" className="btn-secondary justify-center" data-testid="continue-shopping-btn">
              Continue Shopping
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
